import { Select } from "@win-reality/react-ui";
import { useMemo } from "react";

import type { TileOption } from "../../../constants/tile-options";
import {
  getTileTypeLabel,
  type TileTypeKey,
} from "../../../utils/tile-helpers";

interface TileSelectorProps {
  data: TileOption[];
  label: string;
  placeholder: string;
  tileType: TileTypeKey | string;
  onChange: (selectedValue: string, selectedOption: TileOption) => void;
}

export const TileSelector = ({
  data,
  tileType,
  label,
  placeholder,
  onChange,
}: TileSelectorProps) => {
  const isVisualizationType =
    tileType === "visualization" || tileType === "focusedVisualization";
  const isSingleType = tileType === "single";

  const filteredData = useMemo(() => {
    if (isVisualizationType)
      return data.filter((opt) => opt.type === "visualization");
    if (isSingleType) return data;
    return data.filter((opt) => opt.type !== "visualization");
  }, [data, isVisualizationType, isSingleType]);

  const items = useMemo(
    () => filteredData.map((opt) => ({ value: opt.value, label: opt.label })),
    [filteredData],
  );

  const handleChange = (selectedValue: string) => {
    const selectedOption = data.find((opt) => opt.value === selectedValue);
    if (!selectedOption) return;
    onChange(selectedValue, selectedOption);
  };

  const labelWithType = `${label}${getTileTypeLabel(tileType)}`;

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-sm font-semibold tracking-wide">
        {labelWithType}
      </label>
      <Select
        value={undefined}
        onValueChange={handleChange}
        placeholder={placeholder}
        items={items}
      />
    </div>
  );
};
